import React, { useState, useEffect, useRef } from "react";
import "./fbxcalendar.style.scss";
import { formatLocalYMD, getTodayEasternYMD, parseYMDDate } from "../../../utils/dateUtils.ts";

const SimpleCalendar = ({ dateIn = null, onDateChange }) => {
    const fallbackDate = parseYMDDate(getTodayEasternYMD()) || new Date();
    const [selectedDate, setSelectedDate] = useState("");
    const [selectedYear, setSelectedYear] = useState(fallbackDate.getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(fallbackDate.getMonth());
    const [calendarDays, setCalendarDays] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const monthNames = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ];
    const calendarRef = useRef(null);

    useEffect(() => {
        if (dateIn) {
            const date = parseYMDDate(dateIn);
            if (date instanceof Date && !isNaN(date)) {
                setSelectedDate(formatLocalYMD(date));
                setSelectedYear(date.getFullYear());
                setSelectedMonth(date.getMonth());
            }
        }
    }, [dateIn]);

    useEffect(() => {
        const start = new Date(selectedYear, selectedMonth, 1);
        const end = new Date(selectedYear, selectedMonth + 1, 0);
        const pad = Array(start.getDay()).fill("");
        const days = [...pad, ...Array.from({ length: end.getDate() }, (_, i) => i + 1)];
        setCalendarDays(days);
    }, [selectedYear, selectedMonth]);

    const handleDayClick = (day) => {
        if (!day) return;
        const date = new Date(selectedYear, selectedMonth, day);
        const formatted = formatLocalYMD(date);
        setSelectedDate(formatted);
        onDateChange?.(formatted);
        setIsOpen(false);
    };

    const resetDate = () => {
        setSelectedDate("");
        onDateChange?.(null);
    };

    const toggleCalendar = () => {
        setIsOpen(prev => !prev);
    };

    return (
        <div className="fbx-calendar-container" style={{ position: "relative" }}>
            <div className="fbx-calendar-input d-flex" ref={calendarRef} onClick={toggleCalendar}>
                <input
                    type="text"
                    value={selectedDate || ""}
                    placeholder="YYYY-MM-DD"
                    className="fbx-calendar-input-field"
                    readOnly
                />
                <button
                    className="fbx-reset-button"
                    onClick={(e) => {
                        e.stopPropagation();
                        resetDate();
                    }}
                    disabled={!selectedDate}
                    style={{
                        width: "35px",
                        height: "35px",
                        lineHeight: "35px",
                        backgroundColor: selectedDate ? "black" : "white",
                        color: selectedDate ? "yellow" : "transparent",
                        border: "1px solid #ced4da",
                        borderLeft: "none",
                        borderRadius: "0px 4px 4px 0px",
                        cursor: selectedDate ? "pointer" : "not-allowed",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    X
                </button>
            </div>

            {isOpen && (
                <div className="fbx-calendar-popup">
                    <div className="fbx-calendar-header">
                        <button className="btn btn-white" onClick={() => {
                            if (selectedMonth === 0) {
                                setSelectedMonth(11);
                                setSelectedYear(prev => prev - 1);
                            } else {
                                setSelectedMonth(prev => prev - 1);
                            }
                        }}>{"<<"}</button>
                        <span className="fw-bold text-primary" style={{ cursor: "default" }}>{selectedYear}</span>
                        <span className="fw-bold text-primary" style={{ cursor: "default" }}>
                            {monthNames[selectedMonth]}
                        </span>
                        <button className="btn btn-white" onClick={() => {
                            if (selectedMonth === 11) {
                                setSelectedMonth(0);
                                setSelectedYear(prev => prev + 1);
                            } else {
                                setSelectedMonth(prev => prev + 1);
                            }
                        }}>{">>"}</button>
                    </div>
                    <div className="fbx-calendar-days-header">
                        {dayNames.map((day, index) => (
                            <span key={index}>{day}</span>
                        ))}
                    </div>
                    <div className="fbx-calendar-days">
                        {calendarDays.map((day, i) => (
                            <div
                                key={i}
                                className={`fbx-calendar-day ${
                                    day === parseInt(selectedDate.split("-")[2]) ? "selected" : ""
                                }`}
                                onClick={() => handleDayClick(day)}
                            >
                                {day}
                            </div>
                        ))}
                    </div>
                    <div className="fbx-calendar-footer">
                        <button className="btn btn-sm btn-primary w-100" onClick={() => {
                            const formatted = getTodayEasternYMD();
                            const today = parseYMDDate(formatted) || new Date();
                            setSelectedYear(today.getFullYear());
                            setSelectedMonth(today.getMonth());
                            setSelectedDate(formatted);
                            onDateChange?.(formatted);
                            setIsOpen(false);
                        }}>
                            Today
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SimpleCalendar;
